import { useMemo, useState } from 'react';
import { Check, Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SpecialtyOption } from './types';

type AddSpecialtyControlProps = {
  options: SpecialtyOption[];
  selectedIds: string[];
  loading?: boolean;
  disabled?: boolean;
  onAdd: (specialtyId: string) => void;
};

export function AddSpecialtyControl({
  options,
  selectedIds,
  loading = false,
  disabled = false,
  onAdd,
}: AddSpecialtyControlProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const available = useMemo(
    () =>
      options.filter(
        (opt) =>
          !selectedIds.includes(opt.id) &&
          opt.name.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [options, selectedIds, search],
  );

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Add specialty"
        disabled={disabled || loading}
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex h-10 items-center gap-2 rounded-lg border border-dashed border-sage-soft px-3 text-sm font-medium text-sage transition-colors hover:bg-sage-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Plus className="h-4 w-4" />
        Add specialty
      </button>

      {open ? (
        <div className="absolute z-20 mt-2 w-80 rounded-md border bg-white p-2 shadow-lg">
          <div className="mb-2 flex items-center gap-2 rounded-md border px-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search specialties…"
              className="h-9 w-full bg-transparent text-sm outline-none"
              aria-label="Search specialties"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto" role="listbox" aria-label="Available specialties">
            {available.length === 0 ? (
              <li className="px-2 py-3 text-sm text-gray-500">No specialties found</li>
            ) : (
              available.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-gray-50',
                    )}
                    onClick={() => {
                      onAdd(opt.id);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <Check className="h-4 w-4 opacity-0" />
                    {opt.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
